import React, { useState, useEffect, useMemo } from 'react';
import {
    Container, Paper, Typography, Box, Alert, CircularProgress, Link,
    FormControl, InputLabel, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigationBar from '../components/NavigationBar';
import { apiFetch } from '../api/apiFetch';
import { extractMeasurements } from '../utils/exportCsv';
import { isTierGateStatus, tierMessage } from '../api/tierError';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const fmt = (v, dp) => (v === null || v === undefined ? '—' : Number(v).toFixed(dp));

// Metric rows for the side-by-side table. `dir` drives the Δ coloring:
//   'higher' → bigger is better (Cp/Cpk/Pp/Ppk); 'lower' → smaller is better
//   (StdDev/PPM); 'neutral' → show Δ but no verdict (mean, n); 'none' → no Δ.
const METRICS = [
    { label: 'Sample size (n)', get: (d) => extractMeasurements(d).length, dp: 0, dir: 'neutral' },
    { label: 'LSL', get: (d) => num(d.lsl), dp: 4, dir: 'none' },
    { label: 'USL', get: (d) => num(d.usl), dp: 4, dir: 'none' },
    { label: 'Mean', get: (d) => num(d.mean), dp: 4, dir: 'neutral' },
    { label: 'StdDev (overall)', get: (d) => num(d.std_dev), dp: 4, dir: 'lower' },
    { label: 'Cp', get: (d) => num(d.cp), dp: 3, dir: 'higher' },
    { label: 'Cpk', get: (d) => num(d.cpk), dp: 3, dir: 'higher' },
    // M6: the persisted pp/ppk are ALWAYS the classical normal-assumption indices
    // ((USL-LSL)/6σ etc.), independent of the fitted distribution — the Cockpit hides
    // these for a non-normal fit and shows Pp(eq)/Pp(ISO) instead. Label them "(normal)"
    // here so a skewed study's value isn't misread as its actual capability.
    { label: 'Pp (normal)', get: (d) => num(d.pp), dp: 3, dir: 'higher' },
    { label: 'Ppk (normal)', get: (d) => num(d.ppk), dp: 3, dir: 'higher' },
    { label: 'PPM (overall)', get: (d) => num(d.ppm_overall), dp: 0, dir: 'lower' },
];

// Pure delta logic (exported for tests). improved: true (better) / false (worse)
// / null (neutral, equal, or not comparable).
export function metricDelta(dir, va, vb) {
    if (va === null || va === undefined || vb === null || vb === undefined || dir === 'none') {
        return { delta: null, improved: null };
    }
    const delta = vb - va;
    if (dir === 'neutral' || Math.abs(delta) < 1e-9) return { delta, improved: null };
    return { delta, improved: dir === 'higher' ? delta > 0 : delta < 0 };
}

const headSx = { color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.12)', whiteSpace: 'nowrap' };
const cellSx = { color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', borderBottom: '1px solid rgba(255,255,255,0.06)' };

function StudySelect({ label, value, onChange, datasets }) {
    return (
        <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.6)' }}>{label}</InputLabel>
            <Select
                label={label} value={value} onChange={(e) => onChange(e.target.value)}
                sx={{ color: 'rgba(255,255,255,0.9)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.23)' }, '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' } }}
                MenuProps={{ PaperProps: { sx: { backgroundColor: '#1e242c', color: 'rgba(255,255,255,0.85)', maxHeight: 360 } } }}
            >
                {datasets.map((d) => (
                    <MenuItem key={d.id} value={String(d.id)}>#{d.id} — {d.description}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function ComparePage() {
    const [datasets, setDatasets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [aId, setAId] = useState('');
    const [bId, setBId] = useState('');

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                const res = await apiFetch(`${backendUrl}/api/datasets?limit=100&offset=0`, { credentials: 'include' });
                // AUTH-13: surface a tier-gate denial as an upgrade/sign-in prompt.
                if (isTierGateStatus(res.status)) {
                    let body = {};
                    try { body = await res.json(); } catch { /* non-JSON body */ }
                    const tm = tierMessage(res.status, body);
                    setError(tm ? tm.text : 'Access denied.');
                    setLoading(false);
                    return;
                }
                if (!res.ok) throw new Error('fetch failed');
                const data = await res.json();
                const arr = data.datasets || data || [];
                setDatasets(arr);
                // Default: A = second-newest, B = newest (a natural before/after).
                if (arr.length >= 2) { setAId(String(arr[1].id)); setBId(String(arr[0].id)); }
                else if (arr.length === 1) { setAId(String(arr[0].id)); }
            } catch {
                setError('Failed to load studies. Please refresh.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const a = useMemo(() => datasets.find((d) => String(d.id) === aId) || null, [datasets, aId]);
    const b = useMemo(() => datasets.find((d) => String(d.id) === bId) || null, [datasets, bId]);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#121212' }}>
            <NavigationBar />
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>Study Comparison</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
                    Compare two saved analyses side by side (before/after, gauge A vs B). Read-only — Δ is colored by
                    whether B improves on A (higher Cp/Cpk/Pp/Ppk or lower StdDev/PPM is better).
                </Typography>

                {loading && <CircularProgress />}
                {error && <Alert severity="error">{error}</Alert>}
                {!loading && !error && datasets.length < 2 && (
                    <Alert severity="info">
                        You need at least two saved analyses to compare.{' '}
                        <Link component={RouterLink} to="/">Run one in the Cockpit.</Link>
                    </Alert>
                )}

                {!loading && !error && datasets.length >= 2 && (
                    <Paper sx={{ p: 2, backgroundColor: 'rgba(40,44,52,0.92)' }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            <StudySelect label="Study A" value={aId} onChange={setAId} datasets={datasets} />
                            <StudySelect label="Study B" value={bId} onChange={setBId} datasets={datasets} />
                        </Box>

                        {a && b && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headSx}>Metric</TableCell>
                                            <TableCell sx={headSx} align="right">A · {a.description}</TableCell>
                                            <TableCell sx={headSx} align="right">B · {b.description}</TableCell>
                                            <TableCell sx={headSx} align="right">Δ (B−A)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {METRICS.map((m) => {
                                            const va = m.get(a);
                                            const vb = m.get(b);
                                            const { delta, improved } = metricDelta(m.dir, va, vb);
                                            const deltaColor = improved === null
                                                ? 'rgba(255,255,255,0.55)'
                                                : (improved ? '#81c784' : '#e57373');
                                            const deltaText = delta === null
                                                ? '—'
                                                : (delta > 0 ? '+' : '') + delta.toFixed(m.dp);
                                            return (
                                                <TableRow key={m.label}>
                                                    <TableCell sx={cellSx}>{m.label}</TableCell>
                                                    <TableCell sx={cellSx} align="right">{fmt(va, m.dp)}</TableCell>
                                                    <TableCell sx={cellSx} align="right">{fmt(vb, m.dp)}</TableCell>
                                                    <TableCell sx={{ ...cellSx, color: deltaColor, fontWeight: 600 }} align="right">{deltaText}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                )}
            </Container>
        </Box>
    );
}

export default ComparePage;
